/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getActiveTenantFromSessionStorage } from '../app/shared/functions';
import { TENANT_QUERY_PARAM } from './api.service';

/**
 * Adapter interface for serializing/deserializing filter state.
 * Each consumer provides its own implementation.
 */
export interface FilterAdapter<T> {
  /** Serialize typed filter → flat string map for URL/storage */
  toParams(filter: T): Record<string, string>;
  /** Deserialize flat string map → partial typed filter */
  fromParams(params: Record<string, string>): Partial<T>;
  /** Return default filter state */
  getDefaults(): T;
  /** Validate a restored param value. Return null to discard the value. */
  validateParam?(key: string, value: string): string | null;
}

/** Storage key prefix to namespace filter data */
const STORAGE_KEY_PREFIX = 'epg-filter-';
/** Storage key prefix for owner info (user + tenant) */
const OWNER_KEY_PREFIX = 'epg-filter-owner-';

@Injectable({
  providedIn: 'root'
})
export class FilterPersistenceService {
  /**
   * Initialize filter state from URL > sessionStorage > localStorage > defaults.
   * Checks owner (user + tenant) — clears storage if owner changed.
   * Returns the merged filter state.
   */
  initialize<T>(storageKey: string, adapter: FilterAdapter<T>, route: ActivatedRoute): T {
    const defaults = adapter.getDefaults();
    const tenant = getActiveTenantFromSessionStorage();
    const user = this.getCurrentUser();

    // Check owner: if stored owner doesn't match current user/tenant, clear this key
    if (!this.checkOwner(storageKey, user, tenant)) {
      this.removeFromSessionStorage(storageKey);
      this.removeFromLocalStorage(storageKey);
      return defaults;
    }

    // Priority 1: URL query params
    const urlParams = this.readUrlParams(route);
    if (Object.keys(urlParams).length > 0) {
      const validatedParams = this.validateParams(urlParams, adapter);
      const filterFromUrl = adapter.fromParams(validatedParams);
      const merged = this.mergeWithDefaults(filterFromUrl, defaults, adapter);
      // URL takes precedence → save to storage
      this.writeToStorage(storageKey, adapter.toParams(merged));
      this.saveOwner(storageKey, user, tenant);
      return merged;
    }

    // Priority 2: sessionStorage
    const sessionData = this.readFromSessionStorage(storageKey);
    if (sessionData && Object.keys(sessionData).length > 0) {
      const validatedParams = this.validateParams(sessionData, adapter);
      const filterFromSession = adapter.fromParams(validatedParams);
      return this.mergeWithDefaults(filterFromSession, defaults, adapter);
    }

    // Priority 3: localStorage
    const localData = this.readFromLocalStorage(storageKey);
    if (localData && Object.keys(localData).length > 0) {
      const validatedParams = this.validateParams(localData, adapter);
      const filterFromLocal = adapter.fromParams(validatedParams);
      // Promote to sessionStorage
      this.writeToSessionStorage(storageKey, adapter.toParams(this.mergeWithDefaults(filterFromLocal, defaults, adapter)));
      return this.mergeWithDefaults(filterFromLocal, defaults, adapter);
    }

    // Priority 4: defaults
    return defaults;
  }

  /**
   * Persist current filter state to URL query params + sessionStorage + localStorage.
   * Always includes the `tenant` param if available.
   */
  persist<T>(storageKey: string, adapter: FilterAdapter<T>, filter: T, router: Router): void {
    const params = adapter.toParams(filter);

    // Always include tenant
    const tenant = getActiveTenantFromSessionStorage();
    const user = this.getCurrentUser();
    if (tenant) {
      params['tenant'] = tenant;
    }

    // Remove empty/default values from URL to keep it clean
    const urlParams = this.removeEmptyValues(params);

    // Write to URL (replaceUrl to avoid history pollution)
    router.navigate([], {
      replaceUrl: true,
      queryParams: urlParams,
      // no merge: removed filter keys must be dropped from URL
      queryParamsHandling: undefined
    });

    // Write to both storages
    this.writeToStorage(storageKey, params);
    // Save owner info so we can detect user/tenant changes later
    this.saveOwner(storageKey, user, tenant);
  }

  private getCurrentUser(): string | undefined {
    try {
      const user = sessionStorage.getItem('activeUser');
      return user ?? undefined;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * Clear URL params + session + local storage for a specific filter key.
   * Called when the user clicks "Reset".
   */
  reset(storageKey: string, router: Router): void {
    // Clear storage
    this.removeFromSessionStorage(storageKey);
    this.removeFromLocalStorage(storageKey);

    // Clear URL params for this filter (keep tenant)
    const tenant = getActiveTenantFromSessionStorage();
    const queryParams: Record<string, any> = {};
    // We clear all known filter params by navigating with only tenant
    // The consumer should re-navigate with defaults after this
    router.navigate([], {
      replaceUrl: true,
      queryParams: tenant ? { tenant } : {},
      // no merge: overwrite existing query params to clear filter params
      queryParamsHandling: undefined
    });
  }

  /**
   * Clear ALL filter storage across all keys.
   * Called on tenant switch or new user login.
   */
  clearAll(): void {
    // Clear all sessionStorage keys with our prefix
    this.clearStorageByPrefix(sessionStorage, STORAGE_KEY_PREFIX);
    // Clear all localStorage keys with our prefix
    this.clearStorageByPrefix(localStorage, STORAGE_KEY_PREFIX);
    // Clear all owner keys
    this.clearStorageByPrefix(localStorage, OWNER_KEY_PREFIX);
  }

  // --- Private helpers ---

  private readUrlParams(route: ActivatedRoute): Record<string, string> {
    const params: Record<string, string> = {};
    const snapshot = route.snapshot.queryParamMap;
    snapshot.keys.forEach((key) => {
      if (key === TENANT_QUERY_PARAM) {
        return;
      }

      const value = snapshot.get(key);
      if (value !== null && value !== '') {
        params[key] = value;
      }
    });
    return params;
  }

  private validateParams(params: Record<string, string>, adapter: FilterAdapter<any>): Record<string, string> {
    if (!adapter.validateParam) {
      return params;
    }
    const validated: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      const result = adapter.validateParam(key, value);
      if (result !== null) {
        validated[key] = result;
      }
    }
    return validated;
  }

  private mergeWithDefaults<T>(partial: Partial<T>, defaults: T, adapter: FilterAdapter<T>): T {
    return { ...defaults, ...partial };
  }

  private writeToStorage(storageKey: string, params: Record<string, string>): void {
    this.writeToSessionStorage(storageKey, params);
    this.writeToLocalStorage(storageKey, params);
  }

  private writeToSessionStorage(storageKey: string, params: Record<string, string>): void {
    try {
      sessionStorage.setItem(STORAGE_KEY_PREFIX + storageKey, JSON.stringify(params));
    } catch (e) {
      // Storage might be full or unavailable
      console.warn('Failed to write filter to sessionStorage', e);
    }
  }

  private writeToLocalStorage(storageKey: string, params: Record<string, string>): void {
    try {
      localStorage.setItem(STORAGE_KEY_PREFIX + storageKey, JSON.stringify(params));
    } catch (e) {
      console.warn('Failed to write filter to localStorage', e);
    }
  }

  private readFromSessionStorage(storageKey: string): Record<string, string> | null {
    try {
      const data = sessionStorage.getItem(STORAGE_KEY_PREFIX + storageKey);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  private readFromLocalStorage(storageKey: string): Record<string, string> | null {
    try {
      const data = localStorage.getItem(STORAGE_KEY_PREFIX + storageKey);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  private removeFromSessionStorage(storageKey: string): void {
    sessionStorage.removeItem(STORAGE_KEY_PREFIX + storageKey);
  }

  private removeFromLocalStorage(storageKey: string): void {
    localStorage.removeItem(STORAGE_KEY_PREFIX + storageKey);
  }

  private removeEmptyValues(params: Record<string, string>): Record<string, string> {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== '' && value !== undefined && value !== null) {
        result[key] = value;
      }
    }
    return result;
  }

  private saveOwner(storageKey: string, user?: string, tenant?: string): void {
    try {
      localStorage.setItem(
        OWNER_KEY_PREFIX + storageKey,
        JSON.stringify({ user: user ?? '', tenant: tenant ?? '' })
      );
    } catch (e) {
      console.warn('Failed to write filter owner to localStorage', e);
    }
  }

  private checkOwner(storageKey: string, currentUser?: string, currentTenant?: string): boolean {
    // No owner stored yet → first use, accept
    const stored = this.readOwner(storageKey);
    if (!stored) return true;

    // If user changed → reject (different person logged in)
    if (currentUser && stored.user && currentUser.toLowerCase() !== stored.user.toLowerCase()) {
      return false;
    }
    // If tenant changed → reject
    if (currentTenant && stored.tenant && currentTenant.toLowerCase() !== stored.tenant.toLowerCase()) {
      return false;
    }
    return true;
  }

  private readOwner(storageKey: string): { user: string; tenant: string } | null {
    try {
      const data = localStorage.getItem(OWNER_KEY_PREFIX + storageKey);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  private clearStorageByPrefix(storage: Storage, prefix: string): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => storage.removeItem(key));
  }
}
